import { Button } from 'antd';
import { Plus } from 'lucide-react';
import type {
  AssociatedUnitDto,
  CommandAskDto,
  CommandRoleDto,
  OrgUnitSummaryDto,
  RequirementLineInputDto,
  Slot,
} from '@pams/shared';
import { BasicsSection, ExtrasSection } from '@/components/intake/Sections';
import { AttachmentList, type PendingAttachment } from '@/components/attachments/AttachmentList';
import { ATTACHMENT_SECTION_COPY, DUTY_COPY, SECTION_TITLES } from './copy';
import type { FORM_STEPS } from './copy';
import type { ArrangementDraft, ArrangementPaDraft } from './formValues';
import { DutyPointStack } from './components/DutyPointStack';
import type { ArrangementDisclosure } from '@/lib/arrangementShape';
import { remainingByColumn, type ForceGrid as ForceGridModel } from '@/lib/forceGrid';
import type { CoveredColumn } from '@/components/intake/ForceGrid';
import type { SectionDescriptor } from '@/lib/sectionRegistry';

/**
 * ─── THE PA INTAKE'S SECTION REGISTRY ────────────────────────────────────────────
 * The order of the sections, the step each one sits on, when each appears and what each
 * still needs from the operator — AS DATA, in one array. `components/intake/IntakeForm.tsx`
 * renders from it; nothing else decides any of those four things.
 *
 * THREE EDITS THIS FILE IS SHAPED TO MAKE CHEAP, and they are the ones a screen actually
 * gets asked for:
 *
 *   REORDER a section    move the element in the array.
 *   RE-STEP a section    change its `step` — one property, one line, nothing else knows.
 *   COLLAPSE the steps   delete an entry from `FORM_STEPS` and repoint its members. The
 *                        repoint is a COMPILE ERROR until it is done, because `StepKey` is
 *                        derived from `FORM_STEPS` itself.
 *
 * ⚠️ DATA ONLY — NO COMPONENT MAY BE DECLARED AT MODULE SCOPE HERE.
 * `react-refresh/only-export-components` is a warning and `pnpm lint` runs
 * `--max-warnings 0`; a file that exports things and also declares a PascalCase component
 * fails the build. Anything that wants to be a component belongs in `components/intake/Sections`.
 *
 * ⚠️ `validate` IS NOT A PUBLISH AUTHORITY. It decides a tick in the right-hand checklist.
 * The pill, the modal behind it and the Publish button read the SERVER's `previewPublish`
 * and only that — see `lib/sectionRegistry.ts`. The sentences below deliberately match the
 * server's own words so that a future per-section hint cannot invent a second vocabulary,
 * but they are never merged into what stops a publish.
 */

/** ⚠️ DERIVED FROM `FORM_STEPS`, so a `step:` typo below is a compile error rather than a
 *  section that silently never appears (an unknown key resolves to step -1, forever hidden). */
type StepKey = (typeof FORM_STEPS)[number]['key'];

/**
 * The imperative surface, under ONE key, so `visibleWhen` and `validate` stay pure and the
 * registry never has to import the page.
 */
export type ArrangementSectionActions = {
  /** ⚠️ Writes the field AND calls `onValuesChange()` — see the page's `appendRow`. */
  appendPlace: (index: number) => void;
  /**
   * ⚠️ [C3a §3] QUEUES a PDF and fires `flush('attach')`. It must NEVER post `/arrangements`
   * itself — see `pendingAttachments`' header on the page for the two-orders race that causes.
   */
  queueAttachment: (file: File) => void;
  dropQueuedAttachment: (key: string) => void;
  appendShift: (index: number) => void;
  addDutyPoint: () => void;
  duplicateDutyPoint: (index: number) => void;
  removeDutyPoint: (index: number) => void;
  /** ⚠️ Takes the new ID ORDER, never indices — see `DutyPointStack`. */
  reorderDutyPoints: (ids: string[]) => void;
  setRequirementLines: (index: number, lines: RequirementLineInputDto[]) => void;
  /** FR-5.2.6c — a one-off column on ONE card. Never written back to the policy. */
  addAdHocColumn: (index: number, label: string) => void;
  /** ⚠️ [C1c §4b] Which posts may fill ONE one-off column on ONE card. Empty is ANY. */
  restrictAdHocColumn: (index: number, lineIndex: number, postTypeIds: readonly string[]) => void;
  /** Drops a one-off column from ONE card. Never a policy column — see `ForceGrid`. */
  removeAdHocColumn: (index: number, lineIndex: number) => void;
  /**
   * ⚠️ [amended C1a] TWO PARAMETERS. It declared a third, `policyVersionId: string`, that
   * the implementation never took and `DutyPointStack` passed `''` into — so a call site
   * read as a deliberate version pin, in the one file where "who pins the version" is the
   * whole question. Choosing a policy CLEARS the pin and the columns; the server resolves.
   */
  setPolicy: (index: number, policyId: string) => void;
  /** ⚠️ [C1a §2] Re-seed one card from its bound version, keeping every count that survives. */
  useCurrentColumns: (index: number) => void;
  toggleCard: (id: string) => void;
  /**
   * ⚠️ [C2b §8c/§8d] "A COMMAND ENTRY CHANGED" — the page's own `onValuesChange`.
   *
   * `CommandSection` writes with `setFieldValue`, which rc-field-form does NOT report through
   * `onValuesChange`, so every command edit left the form clean and autosave answered
   * `'nothing-to-save'`. It is on this surface rather than passed straight to the card because
   * the registry never imports the page — the same reason every other imperative is here.
   */
  commandEntriesChanged: () => void;
  focusLastIn: (sectionId: string, selector: string) => void;
  /**
   * ⚠️ [C5c §2] SHARE ONE CARD'S COLUMN ACROSS EVERY CARD, OR STOP. The page asks first — the
   * confirmation states what each other card sets aside or loses — and refuses outright when
   * converging would drop named supervisors (§2f).
   */
  toggleShare: (index: number, lineIndex: number) => void;
};

/** Everything ONE card needs to render itself, resolved by the page. [amended D4] */
export type DutyPointView = {
  id: string;
  index: number;
  draft: ArrangementDraft;
  shape: ArrangementDisclosure;
  slots: readonly Slot[];
  grid: ForceGridModel;
  roles: readonly CommandRoleDto[];
  policyName: string;
  /** ⚠️ [PRE-P6 §3a] `v3`, or `null` while the version is still in flight. */
  policyVersionLabel: string | null;
  /** True when this card's columns are not a subset of its policy version's — §5c. */
  forks: boolean;
  /** ⚠️ [C1a §2] The column NAMES behind `forks`, so the sentence can say which. */
  forkColumns: readonly string[];
  /**
   * ⚠️ [C1c §4a] THE POSTS A ONE-OFF COLUMN MAY BE NARROWED TO, AND IT IS `allPostTypes`.
   *
   * `policyColumns.ts` exposes both answers deliberately: `postRows` is what a NEW column may
   * be, and `allPostTypes` is everything the version knows — including archived rows, so a
   * restriction saved last month still renders with its real name rather than as `—`. This is
   * a RESOLVING read, which is the second of the two.
   */
  postTypeOptions: readonly { id: string; name: string }[];
  /**
   * ⚠️ [C1a §2] The policy published a new version while this UNPINNED card was open, and
   * the column set genuinely changed. `undefined` for a pinned card — an existing order is
   * frozen on the version it was raised against ([P2], defended by [FIX-1]) — and
   * `undefined` for a bump that only renamed or reordered, which asks the operator nothing.
   */
  versionMoved?: { label: string; added: readonly string[]; dropped: readonly string[] };
  open: boolean;
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  [C5c §2] WHAT SHARING MEANS FOR THIS CARD.
   *
   *  `coveredColumns`        the columns another card asks for on this one's behalf — a dash here.
   *  `sharedLineIndexes`     this card's own lines that cover every card.
   *  `shareOffered`          the toggle is offered: more than one card, a draft, a writer, and a
   *                          version to converge on (2a).
   *  `policyLockedBySharing` any column is shared, so every card binds one version (2f).
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  coveredColumns: readonly CoveredColumn[];
  sharedLineIndexes: ReadonlySet<number>;
  shareOffered: boolean;
  policyLockedBySharing: boolean;
};

export type ArrangementSectionState = {
  draft: ArrangementPaDraft;
  /** ⚠️ [C3a §3] `undefined` until the first save lands — which is exactly Create page 1. */
  savedArrangementId: string | undefined;
  pendingAttachments: readonly PendingAttachment[];
  /**
   * ⚠️ [AUTO-SAVE] A QUEUED FILE IS WAITING FOR THE SAVE BUTTON, NOT FOR AN UPLOAD IN FLIGHT —
   * `ENABLE_AUTO_SAVE=false` on an order not yet saved. REQUIRED, so the Files section can never
   * tell an operator a file "will be ready in a moment" while it waits for a press nobody has made.
   */
  attachmentsWaitForSave: boolean;
  dutyPoints: readonly DutyPointView[];
  policies: readonly { id: string; name: string; description: string | null; isDefault: boolean }[];
  /** ⚠️ [D4.5 §1b] What an already-stored `policyId` renders as — archived ones included. */
  resolvePolicy: (id: string) => { name: string } | undefined;
  officers: ReadonlyMap<string, { name: string }>;
  orgUnits: ReadonlyMap<string, OrgUnitSummaryDto>;
  /**
   * ⚠️ [C6a §2] The saved record's asked command positions, by DUTY POINT id and then by ask id —
   * FROM THE RECORD, like `officers` above: a division's answer is read-only and never rides back
   * through the form. Per card, because an ask id is unique on its duty point only.
   */
  commandAsks: ReadonlyMap<string, ReadonlyMap<string, CommandAskDto>>;
  /**
   * ⚠️ [C6a review] `awaitedIsLive` OF THE SAVED RECORD — whether an unnamed asked position is still
   * "awaited" or, on an order over or withdrawn, "not named". The record page's own rule, so the
   * builder and the view page cannot state opposite things about one seat.
   */
  awaitedLive: boolean;
  derivedTitle: string | null;
  readOnly: boolean;
  /**
   * ⚠️ [C3c §9a] THE ORDER HAS STARTED, SO ITS IDENTITY IS FIXED — a SEPARATE flag from
   * `readOnly`, never folded into it.
   *
   * `readOnly` means "this OPERATOR may not write"; this means "this RECORD may not change
   * here". Collapsing them would show a permissions message for a lifecycle fact, and send an
   * operator whose access is fine to a super admin about a permission they already hold.
   *
   * ⚠️ WHAT IT LOCKS IS THE IDENTITY ONLY — the order number, its date, its title and the crowd
   * estimate. [C2b §4]'s argument for `corrigendum_seq`, applied to the header: a number that
   * has been circulated ON PAPER cannot be quietly changed once people are working to it.
   *
   * ⚠️ AND IT LOCKS NOTHING ELSE ON STEP 1. Attachments stay open, because [C3a §3] put the PDF
   * control there precisely so an operator can file the signed order — which arrives AFTER the
   * duty starts. Associated units stay open, because [C1b §4d] records that withdrawing sight
   * from a unit that no longer needs it is an ordinary act on a live order. Instructions stay
   * open, because an instruction added mid-operation is an operational act rather than an
   * amendment to what was issued.
   */
  identityLocked: boolean;
  /**
   * ⚠️ [C3c §9b] FILLING HAS BEGUN, SO THE DUTY POINTS' STRUCTURE IS FIXED.
   *
   * The trigger is `status === 'ALLOCATION_IN_PROGRESS'`, which `markWorkBegun` writes the
   * moment the FIRST officer is named — already on the DTO, so this needs no query and no
   * second derivation. (`status !== 'PUBLISHED'` would be true of a DRAFT and lock it against
   * itself; a named-assignment count would be a second answer to a question the status answers.)
   *
   * ⚠️ IT MIRRORS A SERVER REFUSAL THAT ALREADY EXISTS AND LOCKS EXACTLY WHAT THAT REFUSES.
   * `assertPublishedDutyPointsUnchanged` refuses, from PUBLISHED, a change to the duty-point
   * SET, to each point's PLACES, its SCHEDULE and its POLICY VERSION — and deliberately ALLOWS
   * requirement lines, command entries and slot overrides (FR-5.2.13). Until now that refusal
   * surfaced only as a 409 AFTER the operator had typed. Locking more than the server refuses
   * would take away an act the API accepts.
   *
   * ⚠️ THE CLEAR-AND-EDIT MODAL IS PARKED. The client's eventual answer is that an operator may
   * clear every assignment to unlock editing; that is destructive enough to deserve its own
   * pass. Nothing here is built towards it, and it gets no `surfaces.ts` entry — that file is
   * for a branch that EXISTS and is hidden, and there is no branch.
   */
  dutyPointsLocked: boolean;
  /**
   * [C1b §4] The order's OWN resolved associated units — the picker's fallback labels.
   *
   * ⚠️ It comes from the RECORD rather than from the draft: the draft carries ids, and a chip
   * whose id is not in the current search result would otherwise paint as an ObjectId
   * (divergence #84).
   */
  associatedUnits: readonly AssociatedUnitDto[];
  /** §4d — removing an association removes SIGHT, and the confirmation is only true once live. */
  isPublished: boolean;
};

export const ARRANGEMENT_SECTIONS: readonly SectionDescriptor<
  ArrangementSectionState,
  StepKey,
  ArrangementSectionActions
>[] = [
  {
    id: 'basics',
    /* ⚠️ MOVING THIS SECTION TO ANOTHER STEP IS THIS ONE LINE. Nothing else knows. */
    step: 'pa',
    title: SECTION_TITLES.basics,
    navTitle: 'PA details',
    validate: ({ draft }) => ({
      blockers: draft.paDate === '' ? ['Set the order date.'] : [],
      warnings: [],
    }),
    render: (state) => (
      <BasicsSection
        draft={state.draft}
        readOnly={state.readOnly}
        identityLocked={state.identityLocked}
        derivedTitle={state.derivedTitle}
        associatedUnits={state.associatedUnits}
        confirmUnassociate={state.isPublished}
      />
    ),
  },
  {
    /*
     * ⚠️ ONE DESCRIPTOR FOR THE WHOLE STACK, NEVER ONE PER CARD.
     *
     * A registry whose LENGTH changes as the operator clicks `Add duty point` would move
     * `registerSection`, the checklist and `stepOfSection` underneath themselves — and
     * `checklistOf` runs during render. The cards are a repeater INSIDE one section, which
     * is what keeps the registry a literal a reader can hold in their head.
     */
    id: 'dutyPoints',
    step: 'duty',
    title: SECTION_TITLES.dutyPoints,
    navTitle: 'Duty points',
    /*
     * ⚠️ [D4.1] THE ONE BARE SECTION IN THE APP, AND THE REASON IS ARITHMETIC.
     *
     * Its content is a stack of duty point cards, so a card around it is a card around
     * cards — the "Duty points" heading and `Add duty point` become a plain row above the
     * list instead of a card header, and the stack gets the section card's 34px back.
     */
    surface: 'bare',
    /*
     * ⚠️ [C1f §5] THE STACK SCROLLS; THE TAB STRIP AND THIS SECTION'S HEADER DO NOT.
     *
     * The `duty` step holds exactly ONE section — `basics` and `extras` are both on `pa` — so
     * bounding this body is the whole of §5, and the `Add duty point` control stays reachable
     * at the fortieth card as at the first. `IntakeForm` does the rest; nothing here reaches
     * into its JSX.
     */
    body: 'scroll',
    validate: ({ dutyPoints }) => {
      if (dutyPoints.length === 0) return { blockers: ['Add at least one duty point.'], warnings: [] };

      /*
       * ⚠️ EVERY SENTENCE NAMES ITS CARD. With a stack, a blocker the operator cannot trace
       * to one card is a blocker they cannot act on — the same reason `previewPublish`
       * prefixes its own. Capped at three so the rail stays a checklist rather than a wall.
       */
      const blockers: string[] = [];
      const warnings: string[] = [];
      let balanced = 0;

      for (const point of dutyPoints) {
        const where = point.draft.locationNames[0] ?? `Duty point ${String(point.index + 1)}`;
        if (point.draft.placeCount === 0) blockers.push(`${where}: add a place.`);
        if (point.slots.length === 0) blockers.push(`${where}: set the dates and times.`);

        /*
         * ⚠️ [C5c §2] A CARD WHOSE ONLY FORCE IS SHARED IS ASKING FOR SOMEBODY — the anchor's number
         * covers it. The server's publish check says the same (`coveredWithAsk`).
         */
        const asked =
          point.grid.totals.some((total) => total > 0) || point.coveredColumns.some((column) => column.anchorCount > 0);
        if (!asked) blockers.push(`${where}: ask for at least one person.`);

        const remaining = remainingByColumn(point.grid);
        const unaddressed = point.grid.totals.some(
          (total, lineIndex) => total > 0 && !point.grid.cells.some((row) => (row[lineIndex] ?? 0) > 0),
        );
        if (unaddressed) blockers.push(`${where}: say which division provides each column.`);
        if (asked && remaining.every((value) => value === 0)) balanced += 1;

        if (point.forks) warnings.push(`${where}: saving this will create a new version of ${point.policyName}.`);
      }

      const shown = blockers.slice(0, 3);
      if (blockers.length > 3) shown.push(`…and ${String(blockers.length - 3)} more`);

      return {
        blockers: shown,
        warnings,
        detail: `${String(dutyPoints.length)} duty point(s) · ${String(balanced)} balanced`,
      };
    },
    render: (state, actions) => <DutyPointStack state={state} actions={actions} />,
    action: (state, actions) => (
      <Button
        size="small"
        /* ⚠️ REQUIRED. Inside a Form.List's render prop rc-field-form gives a button
           `type="button"` for free; a hand-placed one submits the form. */
        htmlType="button"
        icon={<Plus className="size-3.5" />}
        /* ⚠️ [C3c §9b] Adding a card changes the duty-point SET, which is one of the four things
           `assertPublishedDutyPointsUnchanged` refuses once the order is published. */
        disabled={state.readOnly || state.dutyPointsLocked}
        data-testid="add-duty-point"
        onClick={() => {
          actions.addDutyPoint();
        }}
      >
        {DUTY_COPY.add}
      </Button>
    ),
  },
  {
    id: 'extras',
    /*
     * ⚠️ [amended D4.3] TAB **1**, AND THIS REVERSES D4 §5b RATHER THAN DRIFTING FROM IT.
     *
     * D4 put the PA-level instructions at the bottom of tab 2 because they were the only
     * instructions there were. D4.3 reopens OD-20 and gives each duty point its own, so the
     * two now answer different questions and belong in different places: the GENERAL ones
     * are a header field and sit with the other header fields, and the point-specific ones
     * live inside the card they describe (`components/intake/DutyPointCard.tsx`).
     *
     * It also balances the form. Tab 1 carried five light fields and tab 2 carried the whole
     * card stack; moving one collapsed panel across costs tab 1 nothing it cannot hold.
     *
     * ⚠️ MOVING A SECTION IS CHANGING ONE WORD, and that is the registry working — the
     * step is a KEY, so nothing renumbers and nothing else in this array is touched.
     */
    step: 'pa',
    title: SECTION_TITLES.extras,
    navTitle: 'Instructions',
    validate: ({ draft }) => ({
      blockers: [],
      warnings: draft.instructionCount > 0 ? [] : ['No instructions have been added.'],
      detail: 'Optional',
    }),
    render: (state) => <ExtrasSection readOnly={state.readOnly} />,
  },
  {
    /*
     * ─────────────────────────────────────────────────────────────────────────────────────
     *  ⚠️ [C3a §3] THE PA's OWN PDF, ON TAB 1 — ATTACHABLE BEFORE THE ORDER IS PUBLISHED.
     *
     *  [C2b §3] built attachments on the arrangement DETAIL page, which meant a scanned order
     *  could only be pinned to a record that already existed. §3 puts the same control where an
     *  operator actually has the paper in front of them: the step where they are typing the
     *  order's own header fields.
     *
     *  ⚠️ IT IS A SECTION RATHER THAN A CONTROL WEDGED INTO `BasicsSection`, because the registry
     *  is how a section is added ([P3b.1]) — and because `autosave.test.ts` slices `Sections.tsx`
     *  between `BasicsSection` and `PlacesSection` and requires every `name=` it finds there to be
     *  classified as content or defaulted. A queued file is neither a `Form.Item` nor a form
     *  value, so putting it in that slice would be putting it somewhere the guard cannot read.
     *
     *  ⚠️ AN ORDER WITH NO ATTACHMENT IS AN ORDINARY ORDER, SO ABSENCE IS NEVER A BLOCKER —
     *  the warning follows `extras`' precedent so an optional section stays honestly unticked
     *  rather than falsely complete.
     *
     *  ⚠️ BUT A FILE STILL **WAITING** IS, AND WITHOUT THAT IT WAS DROPPED IN SILENCE.
     *  `useUnsavedGuard` intercepts `beforeunload`, a capture-phase click on an `<a href>` and
     *  `popstate` — and none of those is `navigate()`, which is how publish and discard leave.
     *  The drain is a component effect handling ONE file at a time and it dies at unmount, so
     *  with two queued the second is never POSTed: no prompt, no message, and
     *  `index.tsx`'s stated guarantee that leaving with one queued raises the prompt is false
     *  for exactly the two exits this page performs itself.
     *
     *  ⚠️ IT IS TRANSIENT AND SELF-CLEARING, WHICH IS WHY A BLOCKER IS AFFORDABLE HERE. The
     *  drain empties the queue on its own, so this states a fact about a few hundred
     *  milliseconds rather than asking the operator to do anything — and the alternative is
     *  losing a scanned PA with nothing on screen to say so.
     * ─────────────────────────────────────────────────────────────────────────────────────
     */
    id: 'attachments',
    step: 'pa',
    title: SECTION_TITLES.attachments,
    navTitle: 'Files',
    validate: ({ savedArrangementId, pendingAttachments, attachmentsWaitForSave }) => ({
      blockers:
        pendingAttachments.length > 0
          ? [
              (attachmentsWaitForSave ? ATTACHMENT_SECTION_COPY.waitingForSave : ATTACHMENT_SECTION_COPY.blocked)(
                pendingAttachments.length,
              ),
            ]
          : [],
      warnings: [],
      detail:
        pendingAttachments.length > 0
          ? (attachmentsWaitForSave ? ATTACHMENT_SECTION_COPY.queuedForSave : ATTACHMENT_SECTION_COPY.queued)(
              pendingAttachments.length,
            )
          : savedArrangementId === undefined
            ? 'Optional'
            : 'Optional',
    }),
    render: (state, actions) => (
      <AttachmentList
        arrangementId={state.savedArrangementId}
        canWrite={!state.readOnly}
        pending={state.pendingAttachments}
        onQueue={actions.queueAttachment}
        onDropPending={actions.dropQueuedAttachment}
      />
    ),
  },
];
